'use client'

import { useMemo, useEffect, useState } from 'react'
import * as m from 'motion/react-m'
import { Motion } from '@/lib/motion/lazy-motion'
import Hero from '@/components/content/common/Hero'
import Container from '@/components/structure/Container'
import AppIcon from '@/components/content/app/AppIcon'
import AppPicker from '@/components/ui/AppPicker'
import Progress from '@/components/content/modules/hero/Progress'
import Timestamp from '@/components/ui/Timestamp'
import { Visual } from './Visual'
import { Icon } from '@/icons/Icon'
import { getBrandColors, hexToRgba, isPopulatedObject } from '@/lib/utils'
import type { Timestamps } from '@/types/app'
import type { App, Media } from '@/payload-types'

type AlternativeHeroProps = {
  app: Pick<
    App,
    | 'slug'
    | 'name'
    | 'brandPrimaryColor'
    | 'brandSecondaryColor'
    | 'brandTertiaryColor'
    | 'brandBackgroundColor'
    | 'icon'
    | 'glyph'
  >
  alternativeApps: App[]
  timestamps?: Timestamps
}

type SelectedBranding = {
  brandBackgroundColor: string | null
  branding: {
    primaryColor: string
    secondaryColor: string
    icon: Media | null
    glyph: Media | null
    homepageImage: Media | null
  }
}

export default function AlternativeHero({
  app,
  alternativeApps,
  timestamps,
}: AlternativeHeroProps) {
  const [selectedIndex, setSelectedIndex] = useState(0)

  useEffect(() => {
    if (selectedIndex >= alternativeApps.length) {
      setSelectedIndex(0)
    }
  }, [alternativeApps.length, selectedIndex])

  const selectedAlternative = useMemo<SelectedBranding>(() => {
    const selectedApp = alternativeApps[selectedIndex]

    if (!selectedApp) {
      return {
        brandBackgroundColor: null,
        branding: {
          primaryColor: '#FFFFFF',
          secondaryColor: '#FFFFFF',
          icon: null,
          glyph: null,
          homepageImage: null,
        },
      }
    }

    const { primaryColor, secondaryColor } = getBrandColors(selectedApp)

    const glyph = isPopulatedObject<Media>(selectedApp.glyph) ? selectedApp.glyph : null
    const icon = isPopulatedObject<Media>(selectedApp.icon) ? selectedApp.icon : null
    const homepageImage = isPopulatedObject<Media>(selectedApp.homepageImage)
      ? selectedApp.homepageImage
      : null

    return {
      brandBackgroundColor: selectedApp.brandBackgroundColor ?? null,
      branding: {
        primaryColor,
        secondaryColor,
        icon,
        glyph,
        homepageImage,
      },
    }
  }, [selectedIndex, alternativeApps])

  const gradient = `radial-gradient(70% 100% at 0% 0%, ${hexToRgba(selectedAlternative?.branding.primaryColor ?? '#FFFFFF', 0.35)} 0%, ${hexToRgba(selectedAlternative?.branding.primaryColor ?? '#FFFFFF', 0)} 80%),
        radial-gradient(70% 100% at 100% 0%, ${hexToRgba(selectedAlternative?.branding.secondaryColor ?? '#FFFFFF', 0.35)} 0%, ${hexToRgba(selectedAlternative?.branding.secondaryColor ?? '#FFFFFF', 0)} 80%),
        radial-gradient(100% 100% at 50% 0%, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0) 80%, rgba(0, 0, 0, 0) 100%),
        #000`

  const { backgroundColor } = getBrandColors(app)
  const year = new Date().getFullYear()

  return (
    <Hero gradient={gradient} className="pt-32 md:pt-0">
      {({ animations }) => (
        <Motion>
          <>
            <Container className="relative z-20">
              <m.div
                style={animations}
                className="mx-auto flex w-full flex-col-reverse items-center gap-8 md:flex-col md:gap-10"
              >
                {alternativeApps.length > 0 && (
                  <AppPicker
                    apps={alternativeApps}
                    currentIndex={selectedIndex}
                    setCurrentIndexAction={setSelectedIndex}
                  >
                    <div className="hidden size-11 md:block md:size-14">
                      <AppIcon
                        icon={app.icon}
                        glyph={app.glyph}
                        name={app.name}
                        slug={app.slug}
                        background={backgroundColor}
                        loading="eager"
                      />
                    </div>
                    <div className="relative hidden size-14 items-center justify-center md:flex">
                      <Icon name="alternative" />
                      <Progress
                        total={alternativeApps.length}
                        activeIndex={selectedIndex}
                        setActiveIndex={setSelectedIndex}
                        align="custom"
                        position="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 pt-0.5 pl-0.5"
                        size={56}
                      />
                    </div>
                  </AppPicker>
                )}

                <div className="flex max-w-screen-md flex-col items-center gap-4">
                  <AppIcon
                    icon={app.icon}
                    glyph={app.glyph}
                    name={app.name}
                    slug={app.slug}
                    background={backgroundColor}
                    size={56}
                    className="my-3 block md:hidden"
                    loading="eager"
                  />
                  <h1 className="text-balance text-center text-36 md:text-[56px] md:leading-[64px]">
                    <span className="block">Best {app.name}</span>
                    <span className="block"> Alternatives in {year}</span>
                  </h1>

                  {timestamps && (
                    <Timestamp
                      modified={timestamps.modified}
                      published={timestamps.published}
                      lead="Updated"
                      className="sm:hidden"
                    />
                  )}

                  {alternativeApps.length > 0 && (
                    <p className="max-w-96 text-center text-14 leading-7 text-white/60 md:max-w-none md:text-balance md:text-16">
                      {alternativeApps.length} efficient alternative apps curated by our editors.
                      Chosen for User Interface, Usability, Support, Pricing, and Features.
                    </p>
                  )}
                </div>
              </m.div>
            </Container>
            <Visual
              key="visual-component"
              screenshot={selectedAlternative?.branding.homepageImage?.url ?? ''}
            />
          </>
        </Motion>
      )}
    </Hero>
  )
}
